import { Box, Typography, Snackbar } from '@mui/material';
import { useState, useEffect, lazy, Suspense } from 'react';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import CloseIcon from '@mui/icons-material/Close';

import RendererRegistry from '../../pages/DashboardComponents/TemplateEngine/RendererRegistry';
import MarkdownRenderer from '../../pages/DashboardComponents/TemplateEngine/renderers/MarkdownRenderer';
import ContentPanelHeader from './ContentPanelHeader';
import FocusModeButton from './FocusModeButton';
import { useContentPanelActions } from './useContentPanelActions';
import QuestionDlg from './QuestionDlg/QuestionDlg';
import { useContentPanelContext } from '../../contexts/ContentPanelContext';
import { useNoteOptionsPanel } from '../../pages/DashboardComponents/NoteOptionsPanel/Core/useNoteOptionsPanel';
import userStateEngine2 from '../../controllers/UserState/UserStateEngine2';
import { UserStateKeys } from '../../controllers/UserState/UserStateKeys';
import AttachmentStrip from '../../pages/DashboardComponents/AttachmentStrip/AttachmentStrip';
import LightBox from '../../pages/DashboardComponents/AttachmentStrip/LightBox';
import JxButton from '../../JxControls/JxButtons/JxButton';

import './ContentPanel.css';
import '../../theme/jTheme.css';

// Lazy — only opened via context-menu, no reason to bundle it into every ContentPanel load
const NoteOptionsPanel = lazy(() => import('../../pages/DashboardComponents/NoteOptionsPanel/NoteOptionsPanel'));

const bodyIsJson = (body) => {
    if (!body) return false;
    try {
        JSON.parse(body);
        return true;
    } catch {
        return false;
    }
};

/**
 * ContentPanelInner — renders the full content panel for a single journal entry.
 * Must be rendered inside ContentPanelProvider.
 * @param {object} props.entry
 * @param {string} [props.panelId='default']
 */
export default function ContentPanelInner({ entry, panelId = 'default' }) {
    const isTemplate = entry ? RendererRegistry.resolve(entry.format) !== MarkdownRenderer : false;
    const isJson = entry ? bodyIsJson(entry.body) : false;

    const { panelOpen, panelPos, openPanel, closePanel, setInFocusMode, setIsEditing } = useContentPanelContext();

    const { engine, viewMode, bgMode, inFocusMode, isEditing, deleteOpen, newNoteOpen, newNotePos } = useNoteOptionsPanel(panelId);

    const [localDraftBody, setLocalDraftBody] = useState('');

    useEffect(() => {
        if (isEditing) setLocalDraftBody(engine.getDraftBody(panelId));
    }, [isEditing]);

    const [lightBoxOpen, setLightBoxOpen] = useState(false);
    const [lightBoxDocs, setLightBoxDocs] = useState([]);
    const [lightBoxIndex, setLightBoxIndex] = useState(0);

    // Collapsed state lives here (not in ContentPanelHeader) — both the header
    // itself and the AttachmentStrip below it need to react to it.
    const [collapsed, setCollapsed] = useState(() => userStateEngine2.getValue(UserStateKeys.contentHeaderCollapsed) === 'true');

    const { snackOpen, snackMessage, handleCloseSnack } = useContentPanelActions(entry);

    useEffect(() => {
        if (!entry) return;
        engine.setEntry(entry, isTemplate, panelId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entry?.rk]);

    useEffect(() => {
        setIsEditing(isEditing);
    }, [isEditing]);
    useEffect(() => {
        setInFocusMode(inFocusMode);
    }, [inFocusMode]);

    useEffect(() => {
        const handleContextMenu = (e) => openPanel({ top: e.detail.y, left: e.detail.x });
        window.addEventListener('jerny:contextmenu', handleContextMenu);
        return () => window.removeEventListener('jerny:contextmenu', handleContextMenu);
    }, [openPanel]);

    useEffect(() => {
        // FIXED — passes the live entry, not just panelId, since startEditing now needs it
        const handleEditBody = () => engine.startEditing(entry, panelId);
        window.addEventListener('jerny:editbody', handleEditBody);
        return () => window.removeEventListener('jerny:editbody', handleEditBody);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [engine, entry]);

    const handleLightBox = (documents, index) => {
        setLightBoxDocs(documents.filter((d) => d.mimeType?.startsWith('image/')));
        setLightBoxIndex(index);
        setLightBoxOpen(true);
    };

    /** Toggles header collapsed state and persists to userState */
    const handleToggleCollapse = () => {
        const next = !collapsed;
        setCollapsed(next);
        userStateEngine2.set([{ name: UserStateKeys.contentHeaderCollapsed, value: String(next) }]);
    };

    if (!entry) {
        return (
            <Typography variant="caption" sx={{ color: 'var(--j-muted)', p: 1, display: 'block' }}>
                Select an entry to view content.
            </Typography>
        );
    }

    return (
        <Box
            id="content-panel-root"
            sx={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--j-canvas)', position: 'relative' }}
        >
            {inFocusMode && <FocusModeButton onExit={() => engine.exitFocus(panelId)} />}

            {!inFocusMode && (
                <Box id="content-panel-header" sx={{ flexShrink: 0, p: 1 }}>
                    <ContentPanelHeader
                        entry={entry}
                        isTemplate={isTemplate}
                        isJson={isJson}
                        viewMode={viewMode}
                        bgMode={bgMode}
                        panelId={panelId}
                        collapsed={collapsed}
                        onToggleCollapse={handleToggleCollapse}
                    />
                </Box>
            )}

            {!collapsed && (
                <Box id="content-panel-attachments" sx={{ flexShrink: 0 }}>
                    <AttachmentStrip entry={entry} onLightBox={handleLightBox} panelId={panelId} />
                </Box>
            )}

            <Box
                id="content-panel-body"
                className="content-panel-body"
                onContextMenu={(e) => {
                    e.preventDefault();
                    openPanel({ top: e.clientY, left: e.clientX });
                }}
                sx={{
                    background: bgMode === 'canvas' ? 'var(--j-canvas)' : 'var(--j-bg)',
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    mx: inFocusMode ? 2 : 1,
                    mb: inFocusMode ? 2 : 1.5,
                    p: 1,
                    '&::-webkit-scrollbar': { width: '4px', height: '4px' },
                    '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
                    '&::-webkit-scrollbar-thumb': { bgcolor: '#294766', borderRadius: '2px' },
                }}
            >
                {isEditing ? (
                    <textarea
                        value={localDraftBody}
                        onChange={(e) => {
                            setLocalDraftBody(e.target.value);
                            engine.setDraftBody(e.target.value, panelId);
                        }}
                        autoFocus
                        style={{
                            width: '100%',
                            height: '100%',
                            minHeight: 300,
                            background: 'transparent',
                            border: '0.5px solid #294766',
                            borderRadius: 3,
                            color: '#b0c8e0',
                            fontFamily: 'monospace',
                            fontSize: 13,
                            padding: '8px',
                            resize: 'none',
                            outline: 'none',
                            boxSizing: 'border-box',
                        }}
                    />
                ) : (
                    (() => {
                        const Renderer = RendererRegistry.resolve(entry.format);
                        return <Renderer entry={entry} viewMode={viewMode} />;
                    })()
                )}
            </Box>

            {isEditing && (
                <Box
                    id="content-panel-edit-bar"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: '6px',
                        px: 1.5,
                        py: '5px',
                        borderTop: '0.5px solid #294766',
                        background: '#0b1e2e',
                        flexShrink: 0,
                    }}
                >
                    <JxButton
                        label="Cancel"
                        className="jx-button-sm"
                        icon={<CloseIcon sx={{ fontSize: 13 }} />}
                        onClick={() => engine.cancelEdit(panelId)}
                    />
                    <JxButton
                        label="Save"
                        className="jx-button-sm"
                        icon={<SaveOutlinedIcon sx={{ fontSize: 13 }} />}
                        onClick={() => engine.saveBody(entry, panelId)}
                    />
                </Box>
            )}

            <Snackbar
                open={snackOpen}
                autoHideDuration={2000}
                onClose={handleCloseSnack}
                message={snackMessage}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />

            <QuestionDlg
                open={deleteOpen}
                title="Delete Entry"
                message="Are you sure you want to delete this entry? This cannot be undone."
                buttons={[
                    { label: 'Cancel', onClick: () => engine.closeDelete(panelId) },
                    {
                        label: 'Delete',
                        onClick: () => engine.confirmDelete(entry, panelId),
                        color: '#ff6b6b',
                    },
                ]}
            />

            {panelOpen && (
                <Suspense fallback={null}>
                    <NoteOptionsPanel
                        open={panelOpen}
                        anchorPos={panelPos}
                        onClose={closePanel}
                        entry={entry}
                        isTemplate={isTemplate}
                        isJson={isJson}
                        panelId={panelId}
                    />
                </Suspense>
            )}

            {lightBoxOpen && <LightBox documents={lightBoxDocs} initialIndex={lightBoxIndex} onClose={() => setLightBoxOpen(false)} />}
        </Box>
    );
}
